import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayPeriods, queriesPayrolls, queriesWorkLogs } from "@query";
import {
  useWSModalCustom,
  useWSSnackbar,
  WSAlert,
  WSControl,
  WSDataItem,
  WSElement,
  WSInfoBox,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { Payroll, WorkLog, WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo, useState } from "react";

import {
  bulkActionFactory,
  BulkActionProgress
} from "../../../modules/BulkActions";
import { PAYROLL_RUNS_TYPE } from "../constants";
import { formatPayPeriodRange } from "../utils";

/** Resolves to the created `Payroll` (so the caller can open it), or `undefined`. */
type Props = WSModalCustomProps<Payroll | undefined>;

const ModalRunOffCyclePayroll: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();

  const [payPeriodId, setPayPeriodId] = useState<string | null>(null);
  const [autoApprove, setAutoApprove] = useState(false);
  // Non-null only while the auto-approve batch is running (drives the progress view).
  const [approveProgress, setApproveProgress] =
    useState<BulkActionProgress | null>(null);

  const [createPayroll, createMeta] =
    queriesPayrolls.createPayroll.useMutation();
  // Approving one work log at a time (no bulk endpoint). Suppress the per-call
  // sibling-read invalidation — we approve up to N in a tight loop, then the
  // caller navigates to the new run (which refetches), so one storm of
  // invalidations mid-loop would be wasted work.
  const [approveWorkLog] = queriesWorkLogs.approveWorkLog.useMutation({
    defaultDependenciesLevel: "none"
  });

  const queryPayPeriods = queriesPayPeriods.listPayPeriods.useQueryAll();
  // In-review = submitted-but-unpaid. There is no server-side pay-period filter,
  // so we fetch the in-review set and match `payPeriodId` client-side (same
  // pattern as the payroll-run details + paystub pages).
  const queryWorkLogs = queriesWorkLogs.listWorkLogs.useQueryAll({
    filter: { status: { anyOf: [WorkLogStatus.IN_REVIEW] } }
  });
  // `listPayrolls` has no pay-period filter, so the "a run already exists for
  // this period" guard is derived client-side over the account's runs.
  const queryPayrolls = queriesPayrolls.listPayrolls.useQueryAll({
    filter: { type: { eq: PAYROLL_RUNS_TYPE } }
  });

  const payPeriodOptions = useMemo(
    () =>
      (queryPayPeriods.flatData ?? [])
        .filter(payPeriod => !!payPeriod.id)
        .map(payPeriod => ({
          value: payPeriod.id as string,
          label: payPeriod.payPeriodNumber
            ? `Pay period ${payPeriod.payPeriodNumber} · ${formatPayPeriodRange(
                payPeriod.startDate,
                payPeriod.endDate
              )}`
            : formatPayPeriodRange(payPeriod.startDate, payPeriod.endDate)
        })),
    [queryPayPeriods.flatData]
  );

  const unpaidWorkLogs = useMemo(
    () =>
      payPeriodId
        ? (queryWorkLogs.flatData ?? []).filter(
            workLog => workLog.payPeriodId === payPeriodId
          )
        : [],
    [queryWorkLogs.flatData, payPeriodId]
  );

  // Distinct contractors covered by the unpaid work logs. The v3 `WorkLog` has no
  // payeeId, so `payeeEngagementId` (the contractor↔payer relationship) is the
  // grouping key, falling back to `engagementId`.
  const employeeCount = useMemo(
    () =>
      new Set(
        unpaidWorkLogs
          .map(workLog => workLog.payeeEngagementId ?? workLog.engagementId)
          .filter(Boolean)
      ).size,
    [unpaidWorkLogs]
  );

  const existingRun = useMemo(
    () =>
      payPeriodId
        ? (queryPayrolls.flatData ?? []).find(
            payroll => payroll.payPeriodId === payPeriodId
          )
        : undefined,
    [queryPayrolls.flatData, payPeriodId]
  );

  const isLoadingPeriods = queryPayPeriods.isInitialLoading;
  const hasNoPayPeriods = !isLoadingPeriods && payPeriodOptions.length === 0;
  const isCheckingScope =
    queryWorkLogs.isInitialLoading || queryPayrolls.isInitialLoading;
  const willAutoApprove = autoApprove && unpaidWorkLogs.length > 0;
  const isProcessing = approveProgress !== null || createMeta.isLoading;

  const handleSubmit = async () => {
    if (!payPeriodId || existingRun) {
      return;
    }
    try {
      if (willAutoApprove) {
        setApproveProgress({
          total: unpaidWorkLogs.length,
          processed: 0,
          successful: 0,
          failed: 0
        });
        const bulk = bulkActionFactory<WorkLog>({
          onProgress: setApproveProgress,
          action: async workLog => {
            if (workLog.id) {
              await approveWorkLog({ workLogId: workLog.id });
            }
          }
        });
        await bulk.run(unpaidWorkLogs);
        setApproveProgress(null);
      }

      const payroll = await createPayroll({
        payrollCreate: { type: PAYROLL_RUNS_TYPE, payPeriodId }
      });
      openSnackbar({
        icon: { name: "check" },
        message: "Off-cycle payroll created"
      });
      props.onClose(payroll);
    } catch {
      // The create error surfaces via `createMeta.error` (ErrorAlert below);
      // drop the progress view so the user can retry from the form.
      setApproveProgress(null);
    }
  };

  const renderBody = () => {
    if (isProcessing) {
      return (
        <WSList gap="M">
          <WSLoader.Spinner />
          <WSText>
            {approveProgress
              ? `Approving unpaid work logs… ${approveProgress.processed} of ${approveProgress.total}`
              : "Creating payroll run…"}
          </WSText>
        </WSList>
      );
    }

    if (isLoadingPeriods) {
      return <WSLoader.Spinner />;
    }

    if (hasNoPayPeriods) {
      return (
        <WSInfoBox>
          <WSText.ParagraphSm color="gray500">
            No pay period found. There are no pay periods available to run an
            off-cycle payroll for.
          </WSText.ParagraphSm>
        </WSInfoBox>
      );
    }

    return (
      <WSList gap="M">
        <WSText color="gray500">
          Run a payroll outside the regular schedule. Select the pay period to
          pay work logs for.
        </WSText>

        <WSElement>
          <WSText.ParagraphSm color="gray500" mb="XS">
            Pay period
          </WSText.ParagraphSm>
          <WSSelect
            mode="single"
            options={payPeriodOptions}
            placeholder="Select a pay period"
            value={payPeriodId}
            onChange={value => setPayPeriodId(value ?? null)}
          />
        </WSElement>

        {payPeriodId &&
          !isCheckingScope &&
          (existingRun ? (
            <WSAlert
              icon="alert"
              theme="warning"
              title="Payroll run already exists"
            >
              A payroll run already exists for this pay period.
            </WSAlert>
          ) : unpaidWorkLogs.length > 0 ? (
            <>
              <WSInfoBox>
                <WSList gap="S">
                  <WSDataItem
                    label="Number of work logs"
                    value={String(unpaidWorkLogs.length)}
                  />
                  <WSDataItem
                    label="Number of employees within scope"
                    value={String(employeeCount)}
                  />
                </WSList>
              </WSInfoBox>
              <WSControl
                label="Auto-approve unpaid work logs"
                size="S"
                type="checkbox"
                value={autoApprove}
                onChange={setAutoApprove}
              />
            </>
          ) : (
            <WSInfoBox>
              <WSText.ParagraphSm color="gray500">
                No unpaid work logs found for this pay period.
              </WSText.ParagraphSm>
            </WSInfoBox>
          ))}

        <ErrorAlert context="OffCyclePayroll" error={createMeta.error} />
      </WSList>
    );
  };

  return (
    <WSModal
      actions={{
        buttons: [
          {
            label: "Cancel",
            kind: "Secondary",
            type: "button",
            disabled: isProcessing,
            onClick: () => props.onClose(undefined)
          },
          {
            label: willAutoApprove ? "Approve & run payroll" : "Run payroll",
            loading: isProcessing,
            disabled: !payPeriodId || !!existingRun || hasNoPayPeriods,
            onClick: handleSubmit
          }
        ]
      }}
      size="S"
      title="Run off-cycle payroll"
      onClose={() => props.onClose(undefined)}
    >
      {renderBody()}
    </WSModal>
  );
};

export const useModalRunOffCyclePayroll = () =>
  useWSModalCustom(ModalRunOffCyclePayroll);
